// Listing health as the leader sees it: one source for the copy keys and the
// fix links, shared by the Circle-page checklist, the edit-page hint and the
// Leader Action Center task.
import type { components } from '@leanin/api-client';

// The generated component is `| null` because the leader branch marks the
// field nullable (zod-openapi folds that into the registered schema); a
// present value is the object.
export type CircleListingHealth = NonNullable<components['schemas']['CircleListingHealth']>;
export type ListingChecks = CircleListingHealth['checks'];
export type ProfileField = Extract<ListingChecks['profile'], { status: 'todo' }>['missing'][number];
/** A failing check as a listing reason: a request reason, or `incomplete`
 *  for the profile. The Leader Action Center's task vocabulary. */
export type ListingReason = components['schemas']['LeaderTaskListingHealth']['reason'];

// Every listing message to a leader is keyed by the server's `toldState`
// (hidden only while unanswered requests hide the Circle), so the checklist,
// the hint and each Leader Action Center task agree.
//
/** Whether a failing check is why the Circle is in its told state. Hidden is
 *  told only while unanswered requests hide the Circle, and an incomplete
 *  profile only ever lowers one, so on a Circle told hidden the profile check
 *  is a further fix, never the cause. Copy says "because" only for a cause. */
export function causesToldState(toldState: CircleListingHealth['toldState'], reason: ListingReason): boolean {
  return toldState === 'downranked' || reason === 'unresponsive';
}

// The task titles: an ICU `select` over the reasons a task can carry in that
// told state (`{ reason }`), each a whole translated sentence, naming the
// reason as the cause only where causesToldState holds.
export const LISTING_TITLE_KEY = {
  hidden: 'listingHealth.titleHidden',
  downranked: 'listingHealth.titleDownranked',
} as const satisfies Record<CircleListingHealth['state'], string>;

// The edit-page hint after the leader's last close: one sentence per told
// state around the reason fragments (`listingHealth.reason`, a select taken
// word for word from the titles) of the checks that cause it, then
// `listingHealth.hintAlso` around those of any further failing check.
export const LISTING_HINT_KEY = {
  hidden: 'listingHealth.hintHidden',
  downranked: 'listingHealth.hintDownranked',
} as const satisfies Record<CircleListingHealth['state'], string>;

// The checklist card's subtitle.
export const LISTING_SUBTITLE_KEY = {
  hidden: 'listingHealth.checklist.subtitleHidden',
  downranked: 'listingHealth.checklist.subtitleDownranked',
} as const satisfies Record<CircleListingHealth['state'], string>;

export const LISTING_ACTION_KEY = {
  unresponsive: 'listingHealth.action.unresponsive',
  waiting: 'listingHealth.action.waiting',
  incomplete: 'listingHealth.action.incomplete',
} as const satisfies Record<ListingReason, string>;

export const PROFILE_FIELD_KEY = {
  description: 'listingHealth.checklist.field.description',
  cover: 'listingHealth.checklist.field.cover',
  location: 'listingHealth.checklist.field.location',
  format: 'listingHealth.checklist.field.format',
  language: 'listingHealth.checklist.field.language',
  industry: 'listingHealth.checklist.field.industry',
  cadence: 'listingHealth.checklist.field.cadence',
} as const satisfies Record<ProfileField, string>;

/** The fix for a listing reason; the profile fix lands on `field` (the
 *  first missing one) when the caller knows it. */
export function listingHealthHref(circleId: string, reason: ListingReason, field?: ProfileField): string {
  switch (reason) {
    case 'unresponsive':
    case 'waiting':
      return `/circles/${circleId}/admin?section=requests`;
    case 'incomplete':
      return field === undefined
        ? `/circles/${circleId}/admin?section=overview`
        : `/circles/${circleId}/admin?section=overview&field=${field}`;
  }
}

export interface ListingFix {
  readonly reason: ListingReason;
  readonly href: string;
}

/** The failing checks' fixes, requests first. */
export function listingFixes(circleId: string, checks: ListingChecks): readonly ListingFix[] {
  const { requests, profile } = checks;
  return [
    ...(requests.status === 'todo'
      ? [{ reason: requests.reason, href: listingHealthHref(circleId, requests.reason) }]
      : []),
    ...(profile.status === 'todo'
      ? [{ reason: 'incomplete' as const, href: listingHealthHref(circleId, 'incomplete', profile.missing[0]) }]
      : []),
  ];
}
